import i18n from '../utils/i18n.js';
import config from '../utils/config.js';

class Account {
    constructor() {
        this.currentUser = null;
        this.token = null;
        this.USER_STORAGE_KEY = 'ily-user';
        this.TOKEN_STORAGE_KEY = 'ily-token';
        this.loadUserFromStorage();
    }

    /**
     * Saves user and token to localStorage
     * @param {Object} user - User object
     * @param {string} token - Authentication token
     */
    saveUserToStorage(user, token) {
        try {
            if (user && token) {
                localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(user));
                localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
                this.currentUser = user;
                this.token = token;
            }
        } catch (error) {
            console.warn('Failed to save user to localStorage:', error);
        }
    }

    /**
     * Loads user and token from localStorage
     */
    loadUserFromStorage() {
        try {
            const storedUser = localStorage.getItem(this.USER_STORAGE_KEY);
            const storedToken = localStorage.getItem(this.TOKEN_STORAGE_KEY);
            if (storedUser && storedToken) {
                this.currentUser = JSON.parse(storedUser);
                this.token = storedToken;
            }
        } catch (error) {
            console.warn('Failed to load user from localStorage:', error);
            this.currentUser = null;
            this.token = null;
        }
    }

    /**
     * Clears user and token from localStorage
     */
    clearUserStorage() {
        try {
            localStorage.removeItem(this.USER_STORAGE_KEY);
            localStorage.removeItem(this.TOKEN_STORAGE_KEY);
            this.currentUser = null;
            this.token = null;
        } catch (error) {
            console.warn('Failed to clear user from localStorage:', error);
        }
    }

    /**
     * Decodes JWT payload (no signature verification; used only to build minimal user from token).
     * @param {string} token - JWT token
     * @returns {Object|null} Decoded payload or null
     */
    _decodeJwtPayload(token) {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) return null;
            const payload = parts[1];
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
            const json = atob(padded);
            return JSON.parse(json);
        } catch (e) {
            return null;
        }
    }

    /**
     * Logs in using a token from URL (e.g. link from admin). Saves token and builds minimal user from JWT payload.
     * Same token format as POST /api/auth/verify-code; use as Bearer in API requests.
     * @param {string} token - JWT token from query string
     * @returns {boolean} True if token was valid and session was set
     */
    loginWithToken(token) {
        if (!token || typeof token !== 'string') return false;
        const payload = this._decodeJwtPayload(token.trim());
        if (!payload) return false;
        const userId = payload.userId ?? payload.sub ?? payload.id;
        if (!userId) return false;
        const user = {
            id: userId,
            email: payload.email ?? '',
            name: payload.name ?? null,
            lastname: payload.lastname ?? null,
            language: payload.language ?? null,
            emailVerified: payload.emailVerified ?? false
        };
        this.saveUserToStorage(user, token.trim());
        return true;
    }

    /**
     * Sends verification code to email (for registration or login)
     * @param {string} email - User email
     * @param {string} name - User full name (optional, for registration)
     * @returns {Promise<Object>} Send code response
     */
    async sendVerificationCode(email, name = null) {
        try {
            const baseUrl = config.api.auth;
            const language = i18n.getLanguage();
            const response = await fetch(`${baseUrl}/request-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, name, language })
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Failed to send verification code');
            }

            return data;
        } catch (error) {
            console.error('Send verification code error:', error);
            throw error;
        }
    }

    /**
     * Verifies code and logs in/registers user
     * @param {string} email - User email
     * @param {string} code - Verification code
     * @returns {Promise<Object>} Verification response with user and token
     */
    async verifyCode(email, code) {
        try {
            const baseUrl = config.api.auth;
            const response = await fetch(`${baseUrl}/verify-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, code })
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Code verification failed');
            }

            if (data.user && data.token) {
                this.saveUserToStorage(data.user, data.token);
            }

            return data;
        } catch (error) {
            console.error('Code verification error:', error);
            throw error;
        }
    }

    /**
     * Requests verification code for checkout
     * @param {string} email - User email
     * @returns {Promise<Object>} Request code response
     */
    async requestCode(email) {
        try {
            const baseUrl = config.api.auth;
            const language = i18n.getLanguage();
            const response = await fetch(`${baseUrl}/request-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, language })
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Failed to request verification code');
            }

            return data;
        } catch (error) {
            console.error('Request code error:', error);
            throw error;
        }
    }

    /**
     * Verifies code for checkout and returns user data with token
     * @param {string} email - User email
     * @param {string} code - Verification code
     * @returns {Promise<Object>} Verification response with user and token
     */
    async verifyCodeForCheckout(email, code) {
        try {
            const baseUrl = config.api.auth;
            const response = await fetch(`${baseUrl}/verify-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, code })
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Code verification failed');
            }

            // Save user and token for checkout
            if (data.user && data.token) {
                this.saveUserToStorage(data.user, data.token);
            }

            return data;
        } catch (error) {
            console.error('Code verification for checkout error:', error);
            throw error;
        }
    }

    /**
     * Logs out user and reloads page
     */
    async logout() {
        this.clearUserStorage();
        window.location.reload();
    }

    /**
     * Verifies user email with token
     * @param {string} token - Email verification token
     * @returns {Promise<Object>} Verification response
     */
    async verifyEmail(token) {
        try {
            const baseUrl = config.api.auth;
            const response = await fetch(`${baseUrl}/verify-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token })
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Email verification failed');
            }

            if (this.currentUser && data.user) {
                this.currentUser = data.user;
                this.saveUserToStorage(this.currentUser, this.token);
            }

            return data;
        } catch (error) {
            console.error('Email verification error:', error);
            throw error;
        }
    }

    /**
     * @returns {boolean} True if user is authenticated
     */
    isAuthenticated() {
        return !!this.currentUser && !!this.token;
    }

    /**
     * @returns {Object|null} Current user object or null
     */
    getCurrentUser() {
        return this.currentUser;
    }

    /**
     * @returns {string|null} Authentication token or null
     */
    getToken() {
        return this.token;
    }

    /**
     * @returns {boolean} True if user email is verified
     */
    isEmailVerified() {
        return this.currentUser?.emailVerified || false;
    }

    /**
     * Resends verification email to user
     * @returns {Promise<Object>} Resend response
     */
    async resendVerificationEmail() {
        try {
            if (!this.currentUser || !this.currentUser.id) {
                throw new Error('User not authenticated');
            }
            
            const baseUrl = config.api.auth;
            const language = i18n.getLanguage();
            const response = await fetch(`${baseUrl}/resend-verification-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token}`
                },
                body: JSON.stringify({ userId: this.currentUser.id, language })
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Failed to resend verification email');
            }

            return data;
        } catch (error) {
            console.error('Resend verification email error:', error);
            throw error;
        }
    }

    /**
     * Updates user profile information
     * @param {string} userId - User ID
     * @param {Object} profileData - Profile data (name, lastname, language)
     * @returns {Promise<Object>} Update response
     */
    async updateProfile(userId, profileData) {
        try {
            const baseUrl = config.api.baseUrl;
            const response = await fetch(`${baseUrl}/user/${userId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token}`
                },
                body: JSON.stringify(profileData)
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Profile update failed');
            }

            if (data.user) {
                this.currentUser = data.user;
                this.saveUserToStorage(this.currentUser, this.token);
            }

            return data;
        } catch (error) {
            console.error('Profile update error:', error);
            throw error;
        }
    }
}

export default Account;

